'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export type Role = 'admin' | 'delegate' | 'viewer';

export interface AuthUser {
  id: string;
  name: string;
  role: Role;
}

export interface Permissions {
  canWrite: boolean;
  canPurge: boolean;
  canReflect: boolean;
}

export interface AuthContextValue {
  user: AuthUser | null;
  role: Role;
  permissions: Permissions;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const defaultPermissions: Permissions = {
  canWrite: false,
  canPurge: false,
  canReflect: false,
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  role: 'viewer',
  permissions: defaultPermissions,
  isLoading: true,
  isAuthenticated: false,
  logout: async () => {},
  refresh: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [permissions, setPermissions] = useState<Permissions>(defaultPermissions);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAuth = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me');
      const data = await res.json();

      if (data.user) {
        setUser(data.user);
      } else {
        setUser(null);
      }

      setPermissions(data.permissions || defaultPermissions);
    } catch (error) {
      console.error('Failed to fetch auth state:', error);
      setUser(null);
      setPermissions(defaultPermissions);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check for token in URL on mount
  useEffect(() => {
    const handleTokenLogin = async () => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');

      if (token) {
        try {
          const res = await fetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          });

          if (res.ok) {
            // Remove token from URL without reload
            const url = new URL(window.location.href);
            url.searchParams.delete('token');
            window.history.replaceState({}, '', url.toString());
          }
        } catch (error) {
          console.error('Token login failed:', error);
        }
      }

      // Always fetch current auth state
      await fetchAuth();
    };

    handleTokenLogin();
  }, [fetchAuth]);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
      setUser(null);
      setPermissions(defaultPermissions);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, []);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    await fetchAuth();
  }, [fetchAuth]);

  const value: AuthContextValue = {
    user,
    role: user?.role || 'viewer',
    permissions,
    isLoading,
    isAuthenticated: !!user,
    logout,
    refresh,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
